import { assert } from './errors';
import { DT, dt, isDT } from './util/dateTime';

/*
    YearMonth primitive to index model/plan data and entities.

    Idea credit: Carl @ Watershed
*/

export type YearMonth = Opaque<string, 'YearMonth'>;
export type YearMonthRecord<T> = Record<YearMonth, T>;

export const EARLIEST_DATE = '190001';
export const LATEST_DATE = '219912';

const ymRegex = /^\d\d\d\d\d\d$/;

export const YM = Object.freeze({
    make(from?: Date | DT | string | number, from2?: number): YearMonth {
        // from two integers
        if (typeof from2 === 'number') {
            assert(typeof from === 'number', 'Both arguments must be numbers');
            assert(
                from2 < 12 || from2 > 0,
                'Second argument must be valid month num'
            );
            const yyyy = String(from);
            const mm = String(from2).padStart(2, '0');

            return YM.check(yyyy + mm);
        }

        // from DayJS
        if (isDT(from)) return YM.check(from.format('YYYYMM'));

        // from Date
        if (from instanceof Date) {
            const yyyy = String(from.getUTCFullYear());
            const mm = String(from.getUTCMonth() + 1).padStart(2, '0');
            return YM.check(yyyy + mm);
        }

        // from string
        if (typeof from === 'string') {
            const d = dt(from);
            if (d.isValid()) return YM.check(d.format('YYYYMM'));
            return YM.check(from);
        }

        // from integer
        if (typeof from === 'number') {
            assert(Number.isInteger(from), 'Must be integer');
            return YM.check(String(from));
        }

        // without arguments
        if (typeof from === 'undefined') {
            return YM.check(dt().format('YYYYMM'));
        }

        // if we got here, `from` type must be invalid
        assert(
            false,
            'Must be made from valid Date, DayJS, numbers, or string'
        );
    },

    // Convenience method to return the current YearMonth.
    current(): YearMonth {
        return YM.make();
    },

    check(ym: string): YearMonth {
        assert(ymRegex.test(ym), 'Invalid YearMonth string');
        assert(ym > EARLIEST_DATE, 'Must be later than ' + EARLIEST_DATE);
        assert(ym < LATEST_DATE, 'Must be earlier than ' + LATEST_DATE);

        return ym as YearMonth;
    },

    add(ym: YearMonth, num: number, type: 'm' | 'y' = 'm'): YearMonth {
        if (num < 0) return YM.subtract(ym, -num, type);

        const yCurrent = Number(ym.slice(0, 4));
        const mCurrent = Number(ym.slice(4, 6)); // 1-12

        switch (type) {
            case 'm': {
                const m = mCurrent + num;
                let yQuo = ~~(m / 12);
                let mRem = m % 12;
                if (mRem === 0) {
                    yQuo -= 1;
                    mRem = 12;
                }
                const yNew = yCurrent + yQuo;

                return YM.make(yNew, mRem);
            }
            case 'y': {
                const yNew = yCurrent + num;

                return YM.make(yNew, mCurrent);
            }
        }
    },

    subtract(ym: YearMonth, num: number, type: 'm' | 'y' = 'm'): YearMonth {
        if (num < 0) return YM.add(ym, -num, type);

        const yCurrent = Number(ym.slice(0, 4));
        const mCurrent = Number(ym.slice(4, 6));

        switch (type) {
            case 'm': {
                const m = mCurrent - num;

                if (m > 0) return YM.make(yCurrent, m);

                const yNew = yCurrent + ~~(m / 12) - 1;
                const mRem = 12 + (m % 12);

                return YM.make(yNew, mRem);
            }
            case 'y': {
                const yNew = yCurrent - num;

                return YM.make(yNew, mCurrent);
            }
        }
    },

    min(yms: YearMonth[]): YearMonth {
        return YM.make(Math.min(...yms.map(Number)));
    },

    max(yms: YearMonth[]): YearMonth {
        return YM.make(Math.max(...yms.map(Number)));
    },

    totalMonths(ym: YearMonth): number {
        return YM.year(ym) * 12 + YM.month(ym);
    },

    // Returns difference in number of months between two YearMonths.
    // If first arg is earlier than second arg, returned value will
    // be positive; if later, negative.
    diff(ym1: YearMonth, ym2: YearMonth): number {
        return YM.totalMonths(ym2) - YM.totalMonths(ym1);
    },

    range(start: YearMonth, end: YearMonth) {
        const range: YearMonth[] = [];
        let ym = start;
        while (ym <= end) {
            range.push(ym);
            ym = YM.add(ym, 1);
        }
        return range;
    },

    toDateStr(ym: YearMonth): string {
        const ymStr = String(ym);
        const yyyy = ymStr.slice(0, 4);
        const mm = ymStr.slice(4, 6);
        return `${yyyy}-${mm}-01`;
    },

    toDT(ym: YearMonth): DT {
        return dt(ym, 'YYYYMM');
    },

    toStr(ym: YearMonth): string {
        return YM.toDT(ym).format('MMM YYYY');
    },

    toFullStr(ym: YearMonth): string {
        return YM.toDT(ym).format('MMMM YYYY');
    },

    month(ym: YearMonth): number {
        return Number(ym.slice(4, 6));
    },

    year(ym: YearMonth): number {
        return Number(ym.slice(0, 4));
    },
});
